<script setup>
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import { VDataTableServer } from 'vuetify/labs/VDataTable'
import AntibioticDrawer from '@/views/quality/master/AntibioticDrawer.vue'
import AntibioticService from '@/services/quality/antibiotic.service'
import { antibioticCategoryColor, antibioticCategoryTitle } from '@/enums/quality/antibiotic-category'

definePage({
  meta: {
    subject: 'quality.infection_control.master',
    action: 'antibiotic',
  },
})

const searchQuery = ref('')
const debouncedSearch = ref('')
const isConfirmDialogVisible = ref(false)

let searchTimeout = null

watch(searchQuery, val => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    debouncedSearch.value = val
    page.value = 1
  }, 500)
})

const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order
}

const headers = [
  { title: 'Name', key: 'name' },
  { title: 'Code', key: 'code' },
  { title: 'Category', key: 'category', sortable: false },
  { title: 'Restriction', key: 'restriction', sortable: false },
  { title: 'Routes', key: 'routes', sortable: false },
  { title: 'Status', key: 'status', sortable: false },
  { title: 'Actions', key: 'actions', sortable: false },
]

const {
  data: antibioticListing,
  execute: fetchAntibioticListing,
} = await useApi(createUrl('/masters/antibiotics', {
  query: {
    paged: 1,
    q: debouncedSearch,
    itemsPerPage,
    page,
    sort: sortBy,
    order_by: orderBy,
  },
}))

const antibiotics = computed(() => antibioticListing.value?.data)
const totalAntibiotics = computed(() => antibioticListing.value?.meta?.total)

const isDrawerVisible = ref(false)
const isViewOnly = ref(false)
const antibioticData = ref()

const showAntibiotic = (data = null, viewOnly = false) => {
  antibioticData.value = data
  isDrawerVisible.value = true
  isViewOnly.value = viewOnly
}

watch(isDrawerVisible, value => {
  if (value === false) {
    antibioticData.value = null
    isViewOnly.value = false
  }
})

const refConfirmDialog = ref()

const showConfirmDialog = data => {
  antibioticData.value = data
  isConfirmDialogVisible.value = true
}

const deleteAntibiotic = async (confirm, data) => {
  if (data?.id && confirm) {
    AntibioticService.destroy(
      data.id,
      () => {
        refConfirmDialog.value.onSuccess()
        isConfirmDialogVisible.value = false
        fetchAntibioticListing()
      },
      () => {
        refConfirmDialog.value.onError()
      },
    )
  }
}

watch(isConfirmDialogVisible, value => {
  if (value === false) antibioticData.value = null
})
</script>

<template>
  <section>
    <VCard>
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div class="me-3 d-flex gap-3">
          <AppSelect
            :model-value="itemsPerPage"
            :items="[
              { value: 10, title: '10' },
              { value: 25, title: '25' },
              { value: 50, title: '50' },
              { value: 100, title: '100' },
              { value: -1, title: 'All' },
            ]"
            style="inline-size: 6.25rem;"
            @update:model-value="itemsPerPage = parseInt($event, 10)"
          />
        </div>
        <VSpacer />

        <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
          <div style="inline-size: 10rem;">
            <AppTextField
              v-model="searchQuery"
              placeholder="Search"
              density="compact"
            />
          </div>

          <VBtn
            prepend-icon="tabler-plus"
            @click="showAntibiotic(null)"
          >
            Add Antibiotic
          </VBtn>
        </div>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="antibiotics"
        :items-length="totalAntibiotics"
        :headers="headers"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #item.name="{ item }">
          <div
            class="d-flex align-center cursor-pointer"
            @click="showAntibiotic(item, true)"
          >
            <VAvatar
              size="34"
              variant="tonal"
              class="me-3"
            >
              <span>{{ avatarText(item.name) }}</span>
            </VAvatar>
            <div class="d-flex flex-column">
              <h6 class="text-base">
                {{ item.name }}
              </h6>
            </div>
          </div>
        </template>

        <template #item.category="{ item }">
          <VChip
            v-if="item.category"
            :color="antibioticCategoryColor(item.category)"
            size="small"
            label
          >
            {{ antibioticCategoryTitle(item.category) }}
          </VChip>
          <span
            v-else
            class="text-medium-emphasis"
          >—</span>
        </template>

        <template #item.routes="{ item }">
          <div class="d-flex flex-wrap gap-1">
            <VChip
              v-for="route in (item.routes ?? [])"
              :key="route"
              size="x-small"
              color="secondary"
              variant="tonal"
            >
              {{ route }}
            </VChip>
            <span
              v-if="!item.routes?.length"
              class="text-medium-emphasis"
            >—</span>
          </div>
        </template>

        <template #item.status="{ item }">
          <VChip
            :color="item.status ? 'success' : 'error'"
            size="small"
          >
            {{ item.status ? 'Active' : 'Inactive' }}
          </VChip>
        </template>

        <template #item.actions="{ item }">
          <IconBtn @click="showConfirmDialog(item)">
            <VIcon icon="tabler-trash" />
          </IconBtn>

          <IconBtn @click="showAntibiotic(item)">
            <VIcon icon="tabler-edit" />
          </IconBtn>
        </template>
      </VDataTableServer>
    </VCard>

    <AntibioticDrawer
      v-model:isDrawerOpen="isDrawerVisible"
      :form-data="antibioticData"
      :view-only="isViewOnly"
      @refetch-listing="fetchAntibioticListing"
    />
    <ConfirmDialog
      ref="refConfirmDialog"
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure?"
      confirm-msg="Antibiotic Deleted."
      confirm-title="Deleted!"
      cancel-title="Error!"
      cancel-msg="Something Went Wrong."
      :is-confirm-visible="false"
      :is-error-visible="false"
      :data="antibioticData"
      @confirm="deleteAntibiotic"
    />
  </section>
</template>
